// "Act as client" cues (plan v4 §6 Banner, Expiry UX, Exit): the banner row inside the client header, the
// compact strip for pages outside ClientShell, and one controller (mounted once in Root) that owns the
// frozen dialog, the T-2 min warning with Extend, the title prefix and the start announcement.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { UserRoundCog } from 'lucide-react';
import { useSession, ME_KEY, setMeIfCurrent, IDENTITY_NOTICE_KEY } from '../lib/session';
import { post } from '../lib/api';
import { announce } from '../lib/announce';
import { cn } from '../lib/cn';
import { clock, firstName, impersonationOf, useImpersonationState } from '../lib/impersonation';
import { exitImpersonation, liveUploadCount } from '../lib/impersonationActions';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { toast } from '../ui/Toast';
import type { Me } from '@workdesk/shared/contracts';

/** Exit with the in-flight-uploads confirm (§6 Exit step 1). Shared by the banner, strip, Me page and dialog. */
export function useExit() {
  const qc = useQueryClient();
  const { me } = useSession();
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(0);
  const run = useCallback(async () => {
    if (!me) return;
    setConfirm(0);
    setBusy(true);
    try {
      await exitImpersonation(qc, me.id);
    } catch {
      toast("Couldn't exit. Check your connection and try again.", 'error');
      setBusy(false);
    }
  }, [qc, me]);
  const exit = useCallback(() => {
    const n = liveUploadCount();
    if (n > 0) setConfirm(n);
    else void run();
  }, [run]);
  const confirmDialog = (
    <Dialog
      open={confirm > 0}
      onClose={() => setConfirm(0)}
      alert
      size="sm"
      title="Cancel uploads and exit?"
      description={`${confirm} upload${confirm === 1 ? ' is' : 's are'} still running as ${firstName(me?.name)}. Exiting cancels ${confirm === 1 ? 'it' : 'them'}.`}
      footer={
        <>
          <Button onClick={() => setConfirm(0)}>Keep uploading</Button>
          <Button variant="danger" onClick={() => void run()}>
            Cancel uploads and exit
          </Button>
        </>
      }
    />
  );
  return { exit, busy, confirmDialog };
}

/**
 * The banner. `compact` is the strip for NoAccess, RouteError and the full-page fallback. One line at 360 px:
 * only the name truncates. A labelled region, not a live region (start is announced once by the controller).
 */
export function ImpersonationBanner({ compact, className }: { compact?: boolean; className?: string }) {
  const { me } = useSession();
  const imp = impersonationOf(me);
  const { exit, busy, confirmDialog } = useExit();
  if (!me || !imp) return null;
  return (
    <section
      aria-label="Acting as client"
      className={cn(
        'flex h-11 items-center gap-2 border-b border-warning/40 bg-warning-soft pl-3 text-[14px] text-warning-ink sm:pl-6 lg:pl-8',
        compact && 'rounded-lg border',
        className,
      )}
    >
      <UserRoundCog aria-hidden className="size-4 shrink-0" />
      <p className="flex min-w-0 flex-1 items-baseline gap-1 whitespace-nowrap">
        <span className="shrink-0">Acting as</span>
        <strong className="min-w-0 truncate font-semibold" data-testid="acting-name">
          {me.name}
        </strong>
        {!compact && <span className="shrink-0">· ends {clock(imp.expiresAt)}</span>}
      </p>
      <button
        type="button"
        onClick={exit}
        disabled={busy}
        className="inline-flex h-11 shrink-0 items-center rounded-lg px-4 font-semibold underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-current disabled:opacity-60 sm:mr-2 lg:mr-4"
      >
        {busy ? 'Exiting…' : 'Exit'}
      </button>
      {confirmDialog}
    </section>
  );
}

const WARN_MS = 2 * 60_000;

/** Mounted once (App Root). Owns the frozen dialog, the expiry warning, the title prefix and notices. */
export function ImpersonationController() {
  const { me } = useSession();
  const qc = useQueryClient();
  const imp = impersonationOf(me);
  const st = useImpersonationState();
  const { exit, busy, confirmDialog } = useExit();
  const name = firstName(me?.name);
  // Dismissable so text can be copied; any refused write bumps st.dialog and re-shows it.
  const [dismissed, setDismissed] = useState(0);

  // After a reload caused by an identity change in another tab (§2h).
  useEffect(() => {
    try {
      const n = sessionStorage.getItem(IDENTITY_NOTICE_KEY);
      if (n) {
        sessionStorage.removeItem(IDENTITY_NOTICE_KEY);
        toast(n, 'info');
      }
    } catch {
      /* no storage */
    }
  }, []);

  // Title prefix and one announcement per acting session.
  const announced = useRef<string | null>(null);
  useEffect(() => {
    if (!imp || !me) return;
    const prefix = `[As ${name}] `;
    const apply = () => {
      if (!document.title.startsWith(prefix)) document.title = prefix + document.title.replace(/^\[As [^\]]*\] /, '');
    };
    apply();
    const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(apply) : null;
    const t = document.querySelector('title');
    if (mo && t) mo.observe(t, { childList: true });
    if (announced.current !== me.id) {
      announced.current = me.id;
      announce(`Acting as ${me.name}. Anything you do is recorded as you, on their behalf.`);
    }
    return () => {
      mo?.disconnect();
      document.title = document.title.replace(/^\[As [^\]]*\] /, '');
    };
  }, [imp, me, name]);

  // T-2 min warning with Extend while under maxExpiresAt.
  const expiresAt = imp?.expiresAt;
  const maxAt = imp?.maxExpiresAt;
  useEffect(() => {
    if (!expiresAt || st.ended) return;
    const left = new Date(expiresAt).getTime() - Date.now();
    const canExtend = !!maxAt && new Date(maxAt).getTime() > new Date(expiresAt).getTime() + 1000;
    const fire = () => {
      toast(`Your session as ${name} ends at ${clock(expiresAt)}.`, 'error', {
        action: canExtend
          ? {
              label: 'Extend',
              onClick: () =>
                void post<{ expiresAt: string }>('/api/auth/impersonate/extend', {})
                  .then((r) => {
                    const cur = qc.getQueryData<Me | null>(ME_KEY);
                    const ci = impersonationOf(cur);
                    if (cur && ci) setMeIfCurrent(qc, { ...cur, impersonation: { ...ci, expiresAt: r.expiresAt } });
                    toast(`Extended until ${clock(r.expiresAt)}.`);
                  })
                  .catch(() => undefined),
            }
          : undefined,
      });
    };
    if (left <= 0) return;
    const t = setTimeout(fire, Math.max(0, left - WARN_MS));
    return () => clearTimeout(t);
  }, [expiresAt, maxAt, st.ended, name, qc]);

  return (
    <>
      <Dialog
        open={st.ended && st.dialog > dismissed}
        onClose={() => setDismissed(st.dialog)}
        alert
        size="sm"
        title={`Your session as ${st.actingName ?? 'the client'} ended`}
        description="Copy anything you need, then Exit."
        footer={
          <Button variant="primary" loading={busy} onClick={exit}>
            Exit
          </Button>
        }
      />
      {confirmDialog}
    </>
  );
}
